import React, { useEffect, useRef, useState } from 'react'
import { Document, Page } from 'react-pdf'
import 'lib/pdfWorker'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import {
	TransformComponent,
	TransformWrapper,
	type ReactZoomPanPinchRef,
} from 'react-zoom-pan-pinch'
import { AlertTriangle, Download, Loader2 } from 'lucide-react'
import { Button } from 'components/ui/button'
import type { InlineUrlsResponse } from '../types/documentLibrary.types'

// Worker configuration lives in lib/pdfWorker, imported above, so this pane and
// the legacy DocumentPreviewPopupCN cannot drift apart in how they set it.

interface DocumentPreviewPaneProps {
	inlineUrls: InlineUrlsResponse | null
	pageNumber: number
	onLoadSuccess: (info: { numPages: number }) => void
	onDownload?: () => void
	transformRef?: React.MutableRefObject<ReactZoomPanPinchRef | null>
}

const Spinner: React.FC = () => (
	<div className="flex items-center justify-center h-full text-gray-400">
		<Loader2 className="w-8 h-8 animate-spin" />
	</div>
)

const FailureState: React.FC<{
	title: string
	detail?: string | null
	onDownload?: () => void
}> = ({ title, detail, onDownload }) => (
	<div className="flex flex-col items-center justify-center h-full text-gray-600 px-6 text-center gap-3">
		<AlertTriangle className="w-10 h-10 text-amber-500" />
		<div className="text-base font-semibold text-gray-900">{title}</div>
		{detail && <div className="text-xs text-gray-500 max-w-md">{detail}</div>}
		{onDownload && (
			<Button type="button" onClick={onDownload} className="mt-1 gap-1">
				<Download className="w-4 h-4" />
				Download to view
			</Button>
		)}
	</div>
)

const DocumentPreviewPane: React.FC<DocumentPreviewPaneProps> = ({
	inlineUrls,
	pageNumber,
	onLoadSuccess,
	onDownload,
	transformRef,
}) => {
	const paneRef = useRef<HTMLDivElement>(null)
	const [paneHeight, setPaneHeight] = useState(0)

	useEffect(() => {
		if (!paneRef.current) return
		const observer = new ResizeObserver(entries => {
			for (const e of entries) setPaneHeight(e.contentRect.height)
		})
		observer.observe(paneRef.current)
		return () => observer.disconnect()
	}, [])

	if (!inlineUrls) {
		return (
			<div ref={paneRef} className="h-full bg-gray-100">
				<Spinner />
			</div>
		)
	}

	if (inlineUrls.previewStatus === 'pending') {
		return (
			<div ref={paneRef} className="h-full bg-gray-100">
				<FailureState
					title="Preview being prepared…"
					detail="Conversion is still in flight. Re-open the dialog in a moment."
					onDownload={onDownload}
				/>
			</div>
		)
	}

	if (inlineUrls.previewStatus === 'failed' || !inlineUrls.previewUrl) {
		return (
			<div ref={paneRef} className="h-full bg-gray-100">
				<FailureState
					title="Preview unavailable"
					detail={
						inlineUrls.previewError ||
						"This file format can't be previewed. Download the original to view it."
					}
					onDownload={onDownload}
				/>
			</div>
		)
	}

	const family = inlineUrls.family

	if (family === 'pdf' || family === 'office') {
		return (
			<div ref={paneRef} className="h-full overflow-hidden bg-gray-100">
				<Document
					file={inlineUrls.previewUrl}
					onLoadSuccess={onLoadSuccess}
					loading={<Spinner />}
					error={
						<FailureState
							title="PDF failed to load"
							detail="The presigned URL may have expired — close and reopen the preview."
							onDownload={onDownload}
						/>
					}
				>
					{paneHeight > 0 && (
						<TransformWrapper
							minScale={1}
							maxScale={6}
							centerOnInit
							doubleClick={{ disabled: true }}
							ref={transformRef as any}
						>
							<TransformComponent
								wrapperStyle={{ width: '100%', height: paneHeight }}
								contentStyle={{
									width: '100%',
									height: paneHeight,
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
								}}
							>
								<Page
									pageNumber={pageNumber}
									height={Math.max(100, paneHeight - 16)}
									renderTextLayer
									renderAnnotationLayer
								/>
							</TransformComponent>
						</TransformWrapper>
					)}
				</Document>
			</div>
		)
	}

	if (family === 'image') {
		return (
			<div
				ref={paneRef}
				className="h-full overflow-hidden bg-gray-900 flex items-center justify-center"
			>
				<TransformWrapper
					minScale={1}
					maxScale={6}
					centerOnInit
					doubleClick={{ disabled: true }}
					ref={transformRef as any}
				>
					<TransformComponent
						wrapperStyle={{ width: '100%', height: '100%' }}
						contentStyle={{
							width: '100%',
							height: '100%',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
						}}
					>
						<img
							src={inlineUrls.previewUrl}
							alt={inlineUrls.originalFilename}
							className="max-w-full max-h-full object-contain"
						/>
					</TransformComponent>
				</TransformWrapper>
			</div>
		)
	}

	if (family === 'video') {
		return (
			<div
				ref={paneRef}
				className="h-full overflow-hidden bg-black flex items-center justify-center"
			>
				<video
					src={inlineUrls.previewUrl}
					controls
					className="max-w-full max-h-full"
				>
					Your browser doesn't support the video tag.
				</video>
			</div>
		)
	}

	return (
		<div ref={paneRef} className="h-full bg-gray-100">
			<FailureState
				title="Unsupported file type"
				detail="Download the file to view it in its native application."
				onDownload={onDownload}
			/>
		</div>
	)
}

export default DocumentPreviewPane
